import React, { useEffect, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { CContainer, CCard, CCardBody, CFormInput, CFormLabel, CFormRange, CRow, CCol, CButton } from "@coreui/react";
import settingsStore from "../../store/SettingsStore";
import periodPrefsStore from "../../store/PeriodPrefsStore";
import ServerSelect from "../UI/PeriodFilters/ServerSelect";
import ErrorAlert from "../UI/Status/ErrorAlert";
import { SkeletonChart } from "../UI/Skeleton/Skeleton";
import { getSalesForecast } from "../../api/reportsApi";
import { SalesForecastView } from "../Assistant/ToolResultView";
import { logReportView } from "../../api/reportLogApi";

const CITY_STORAGE_KEY = "dash:forecastCity";

const ForecastPage = observer(() => {
  const [selectedServer, setSelectedServer] = useState(periodPrefsStore.selectedServer);
  const [city, setCity] = useState(() => {
    try {
      return localStorage.getItem(CITY_STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });
  const [days, setDays] = useState(7);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!selectedServer || !city.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getSalesForecast(selectedServer, city.trim(), days);
      if (data.ok === false) setError({ message: data.error });
      else setResult(data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [selectedServer, city, days]);

  useEffect(() => {
    const init = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) await settingsStore.getServers(userId);
      logReportView({ event: "report_view", reportKey: "forecast", reportTitle: "Прогноз продаж", userId, path: "/forecast" });
    };
    init();
  }, []);

  useEffect(() => {
    if (!settingsStore.servers?.length) return;
    if (!selectedServer) setSelectedServer(String(settingsStore.servers[0].id));
  }, [settingsStore.servers, selectedServer]);

  useEffect(() => {
    if (selectedServer) periodPrefsStore.setServer(selectedServer);
  }, [selectedServer]);

  useEffect(() => {
    if (selectedServer && city.trim()) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServer]);

  const handleBuild = () => {
    try {
      localStorage.setItem(CITY_STORAGE_KEY, city.trim());
    } catch {
      // ignore
    }
    load();
  };

  return (
    <CContainer style={{ flex: "1", marginTop: "120px" }}>
      <h4 className="mb-3">Прогноз продаж</h4>
      <p className="text-body-secondary small">
        Эвристика: среднее по дню недели за 8 недель истории, скорректированное на производственный календарь и прогноз
        погоды — грубый ориентир, не ML-модель.
      </p>
      <ServerSelect servers={settingsStore.servers} value={selectedServer} onChange={(e) => setSelectedServer(e.target.value)} />
      <CRow className="g-2 align-items-end mb-3">
        <CCol xs={12} md={4}>
          <CFormLabel>Город</CFormLabel>
          <CFormInput value={city} onChange={(e) => setCity(e.target.value)} placeholder="например, Челябинск" />
        </CCol>
        <CCol xs={12} md={4}>
          <CFormLabel>На сколько дней: {days}</CFormLabel>
          <CFormRange min={1} max={14} value={days} onChange={(e) => setDays(Number(e.target.value))} />
        </CCol>
        <CCol xs={12} md="auto">
          <CButton color="primary" onClick={handleBuild} disabled={loading || !city.trim()}>
            Построить прогноз
          </CButton>
        </CCol>
      </CRow>
      <ErrorAlert error={error} />
      <CCard>
        <CCardBody>
          {loading && !result && <SkeletonChart />}
          {!loading && result && <SalesForecastView result={result} />}
          {!loading && !result && !error && (
            <div className="text-body-secondary small">Укажите город и нажмите «Построить прогноз».</div>
          )}
        </CCardBody>
      </CCard>
    </CContainer>
  );
});

export default ForecastPage;
